import React, { FC, Dispatch, SetStateAction, useState, useRef, useMemo, useEffect } from 'react'
import { Box, IconButton, Typography, Paper, ClickAwayListener, Popper, Skeleton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Drawer, Wrapper, ProductsContainer, ProductSkeleton, searchProductsPaperSx } from './ssmDrawer.styles'
import { ConfirmRemoveProps, DrawerProps, SSM_EMPTY_BANNER_CLOSED } from '../SSM'
import ProductAccordion from './product-accordion/ProductAccordion'
import {
  AddVendorPayload,
  SSMAssetType,
  SSMCell,
  SSMCellProduct,
  SSMGroupedData,
  SSMNistPillar,
  SSMProductStatus,
  SSMResponseProduct,
  SSMResponseProductExtended,
  Vendor
} from 'models'
import ThemeButton from '../../../components/buttons/theme/Button'
import theme from 'theme'
import SearchProducts from './add-product/SearchProducts'
import { useAutocomplete } from '@mui/base/useAutocomplete'
import debounce from 'lodash.debounce'
import axios from '../../../../lib/axios'
import { addVendorToRecentSearches, CostDistribution, findProductCells } from '../securityStackMap.utils'
import dayjs from 'dayjs'
import { dateFormat, getIntegrationLogoSSM } from '../../../../lib/utils'
import { notification } from '../../../../store/notifications/notificationSlice'
import { useAppDispatch } from '../../../../store/hooks'
import AddProductForm from './add-product/AddProductForm'
import SSMDrawerViewToggle from './view-toggle/SSMDrawerViewToggle'
import AddCustomProductForm from './add-product/AddCustomProductForm'
import {
  getUTCYear,
  emptyVendorValue,
  initialFormData,
  initialCustomVendorFormData,
  getCostDistribution,
  buildCellsPayload,
  capitalizeCellSegment
} from './ssm.utils'
import { calculateContractPeriods } from './add-product/add-product.utils'

export interface LicensePerYear {
  licenseCost?: number
  numberOfLicenses?: number
  year: number
}

export interface FormData {
  licenseCost?: number
  numberOfLicenses?: number
  pricingUnits?: string
  perYear: LicensePerYear[]
  note?: string
  startDate?: string
  endDate?: string
}

export interface CustomVendorFormData {
  vendorName?: string
  productName?: string
  licenseCost?: number
  numberOfLicenses?: number
  pricingUnits?: string
  perYear: LicensePerYear[]
  note?: string
  startDate?: string
  endDate?: string
}

interface Props {
  drawerProps: DrawerProps
  drawerOpen: boolean
  setDrawerOpen: Dispatch<SetStateAction<boolean>>
  allProducts: SSMResponseProduct[]
  setEmptyStateBanner: Dispatch<SetStateAction<boolean>>
  groupedData: SSMGroupedData
  setGroupedData: Dispatch<SetStateAction<SSMGroupedData>>
  updateDrawerProps: (nist: SSMNistPillar, asset: SSMAssetType, assets: SSMResponseProduct[]) => void
  fetchAndGroupData: (year?: string) => Promise<SSMResponseProduct[] | undefined>
  cell: SSMCell | null
  setCell: Dispatch<SetStateAction<SSMCell | null>>
  contractYear: string
  setConfirmRemoveProps: Dispatch<SetStateAction<ConfirmRemoveProps>>
  fetchCellDetails: (nist: string, asset: string) => void
  fetchingCell: boolean
}

const SSMDrawer: FC<Props> = ({
  drawerProps,
  drawerOpen,
  setDrawerOpen,
  allProducts,
  setEmptyStateBanner,
  groupedData,
  setGroupedData,
  updateDrawerProps,
  fetchAndGroupData,
  cell,
  setCell,
  contractYear,
  setConfirmRemoveProps,
  fetchCellDetails,
  fetchingCell
}) => {
  const { nist, asset } = drawerProps
  const [expandedUuid, setExpandedUuid] = useState<string | false>(false)
  const [addProductOpen, setAddProductOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState(false)
  const [productPopupOpen, setProductPopupOpen] = useState(false)
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [initialFormDataState, setInitialFormDataState] = useState<FormData>(initialFormData)
  const [submitting, setSubmitting] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<SSMResponseProduct | null>(null)
  const [costDistribution, setCostDistribution] = useState<CostDistribution>(CostDistribution.Even)
  const [expenseCalculationFields, setExpenseCalculationFields] = useState<any[]>([])
  const [initialCostDistribution, setInitialCostDistribution] = useState<CostDistribution>(CostDistribution.Even)
  const [initialExpenseCalculationFields, setInitialExpenseCalculationFields] = useState<any[]>([])
  const [addingCustomVendor, setAddingCustomVendor] = useState(false)
  const [customVendorFormData, setCustomVendorFormData] = useState<CustomVendorFormData>(initialCustomVendorFormData)
  const [customVendorId, setCustomVendorId] = useState('')
  const [customVendorOptions, setCustomVendorOptions] = useState<Vendor[]>([])
  const [isNewProduct, setIsNewProduct] = useState(false)
  const [loadingProductData, setLoadingProductData] = useState(false)
  const [ssmProductCellsMap, setSsmProductCellsMap] = useState<Record<string, any[]>>({})

  const addProductButtonRef = useRef<HTMLButtonElement>(null)
  const dispatch = useAppDispatch()

  // Fetch cells data for all products so cost distribution works correctly
  // (groupedData may not reflect all cells when viewing a historical year)
  useEffect(() => {
    if (!cell?.products || cell.products.length === 0) return

    const fetchCells = async () => {
      const newMap: Record<string, any[]> = {}
      await Promise.all(
        cell.products.map(async (product) => {
          try {
            const res = await axios.get(`/api/v3/ssm/product/${product.uuid}`)
            if (res.status === 200 && res.data?.cells) {
              newMap[product.uuid] = res.data.cells
            }
          } catch {
            // Product may not exist in SSM anymore - skip
          }
        })
      )
      setSsmProductCellsMap((prev) => ({ ...prev, ...newMap }))
    }
    fetchCells()
  }, [cell])

  // Get product instances from cached SSM product cells API
  const getProductInstances = (product: SSMCellProduct): SSMResponseProductExtended[] => {
    const cachedCells = ssmProductCellsMap[product.uuid]
    if (cachedCells && cachedCells.length > 0) {
      return cachedCells
        .map(
          (cell: any) =>
            ({
              uuid: product.uuid,
              name: product.name,
              display_name: product.display_name,
              nist: capitalizeCellSegment(cell.nist),
              asset: capitalizeCellSegment(cell.asset),
              costBps: cell.costBps || 0,
              cost: cell.cost ?? 0
            } as unknown as SSMResponseProductExtended)
        )
        .sort((a: any, b: any) => (a.cost < b.cost ? -1 : 1))
    }

    return []
  }

  const closeDrawer = () => {
    setDrawerOpen(false)
    setAddProductOpen(false)
    setProductPopupOpen(false)
    setEditingProduct(false)
    setAddingCustomVendor(false)
    setCell(null)
    setExpandedUuid(false)
    setAlignment('left')
    setCustomVendorOptions([])
  }

  const [backspacingInput, setBackspacingInput] = useState(false)
  const [initialSearched, setInitialSearched] = useState(false)
  const [vendors, setVendors] = useState<Vendor[]>([emptyVendorValue])
  const [value, setValue] = useState<Vendor>(emptyVendorValue)
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [alignment, setAlignment] = useState<string | null>('left')
  const recentProductsStringified =
    typeof window !== 'undefined' ? localStorage.getItem('recently_added_vendors') : null
  const recentProducts = recentProductsStringified ? JSON.parse(recentProductsStringified) : null

  const updateCostDistribution = (newValue: CostDistribution, asset: SSMResponseProduct) => {
    if (window && typeof window !== 'undefined') {
      const costDistribution = localStorage.getItem('cost_distribution')
      const costDistributionArr = costDistribution ? JSON.parse(costDistribution) : []
      const cdObj = {
        ...asset,
        costDistributionValue: newValue
      }
      const foundRes = costDistributionArr.find((n: any) => n.uuid === cdObj.uuid)
      const cdArr = foundRes
        ? [...costDistributionArr.filter((n: any) => n.uuid !== cdObj.uuid), cdObj]
        : [...costDistributionArr, cdObj]
      localStorage.setItem('cost_distribution', JSON.stringify(cdArr))
    }
  }

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault()
      setAddProductOpen(false)
    } else if (event.key === 'Escape') {
      setAddProductOpen(false)
    }
  }

  const handleSearchVendors = async (newValue: string | null): Promise<void> => {
    setInputValue(newValue || '')

    if (!newValue) {
      setValue(emptyVendorValue)
      setVendors([emptyVendorValue])
      setLoading(false)

      return
    }

    if (newValue.length < 2) return

    try {
      const res = await axios.get(`/api/v3/vendors_products/search?name=${newValue}`)

      if (res.status === 200) {
        // Use cell products to check if product already exists in current cell (exclude ended products)
        const thisCellProductIds =
          cell?.products.filter((n) => n.status !== SSMProductStatus.Ended).map((n) => n.uuid) || []
        const d = res.data.data
        const products =
          d.length > 0
            ? d.map((n: any) => (thisCellProductIds.includes(n.uuid) ? { ...n, existsInCell: true } : n))
            : [emptyVendorValue]

        setVendors(products.length > 0 ? products : [emptyVendorValue])
      }
    } catch (e) {
      console.error('Failed searching vendors', e)
      setVendors([emptyVendorValue])
    } finally {
      setLoading(false)
    }
  }

  const debouncedSearch = useMemo(() => debounce(handleSearchVendors, 200), [cell])

  const { getRootProps, getInputProps, getListboxProps, getOptionProps, focused } = useAutocomplete({
    id: 'search-vendors-input',
    options: vendors,
    autoComplete: true,
    getOptionLabel: (option) => option.display_name,
    value,
    onChange: (e, newValue) => setValue(newValue || emptyVendorValue),
    inputValue,
    onInputChange: (e, newInputValue) => setInputValue(newInputValue),
    blurOnSelect: true,
    isOptionEqualToValue: (option) => option.display_name === value.display_name
  })

  useEffect(() => {
    let active = true
    if (backspacingInput) return

    if (inputValue === '') {
      setVendors(value ? [value] : [emptyVendorValue])
      setInputValue('')
    }

    if (active && inputValue.length >= 2) {
      setLoading(true)
      setInitialSearched(true)
    }
    debouncedSearch(inputValue)

    return () => {
      active = false
    }
  }, [value, inputValue])

  const handleClearSearch = () => {
    if (!inputValue) {
      return
    }

    setInputValue('')
    setVendors([emptyVendorValue])
    setValue(emptyVendorValue)
    setLoading(false)
  }

  const updateProductLicense = async (vendorId: string) => {
    // Calculate all years that should be in the payload based on date range
    const perYearPayload = formData.perYear.map((c) => ({
      year: c.year,
      cost: c.licenseCost ?? 0,
      count: c.numberOfLicenses ?? 0
    }))

    // Ensure all years in the date range are included
    if (formData.startDate && formData.endDate) {
      const startYear = getUTCYear(formData.startDate)
      const numPeriods = calculateContractPeriods(formData.startDate, formData.endDate)
      const expectedYears = Array.from({ length: numPeriods }, (_, i) => startYear + i)

      // Check if we need to fill in missing years
      const existingYears = perYearPayload.map((p) => p.year)
      const missingYears = expectedYears.filter((y) => !existingYears.includes(y))

      if (missingYears.length > 0) {
        // Use first entry's values for missing years (collapsed mode behavior)
        const firstEntry = perYearPayload[0] || { cost: 0, count: 0 }
        missingYears.forEach((year) => {
          perYearPayload.push({
            year,
            cost: firstEntry.cost,
            count: firstEntry.count
          })
        })
        // Sort by year
        perYearPayload.sort((a, b) => a.year - b.year)
      }

      // Remove years that are no longer in the expected range
      const validYears = perYearPayload.filter((p) => expectedYears.includes(p.year))
      perYearPayload.length = 0
      perYearPayload.push(...validYears)
    }

    const payload: any = {
      itemId: vendorId,
      startDate: formData.startDate,
      endDate: formData.endDate,
      perYear: perYearPayload
    }

    // Only include entityType if it has a value
    if (formData.pricingUnits) {
      payload.entityType = formData.pricingUnits
    }

    // Only include note if it has a value
    if (formData.note) {
      payload.note = formData.note
    }

    const cells = buildCellsPayload(costDistribution, expenseCalculationFields)
    if (cells) {
      payload.cells = cells
    }

    await axios.post('/api/v3/ssm/license', payload)
  }

  const handleAddVendor = async (
    vendorId: string,
    nist: SSMNistPillar,
    asset: SSMAssetType,
    recentProductsAdd?: boolean,
    option?: any
  ) => {
    setSubmitting(true)

    try {
      const vendorExists = allProducts.find((n) => n.uuid === vendorId)

      setEmptyStateBanner(false)
      localStorage.setItem(SSM_EMPTY_BANNER_CLOSED, 'false')
      if (vendorExists) {
        addVendorToRecentSearches(vendorExists)
      } else if (!vendorExists && option) {
        addVendorToRecentSearches(option)
      }
      const cd = localStorage.getItem('cost_distribution')
      let vendorcd = vendorExists ? CostDistribution.Even : CostDistribution.Manual
      if (cd) {
        const parsed = JSON.parse(cd)
        if (Array.isArray(parsed) && parsed.length > 0) {
          const found = parsed.find((n) => n.uuid === vendorId)
          if (found) {
            vendorcd = found.costDistributionValue || CostDistribution.Even
          }
        }
      }
      if (vendorExists) {
        updateCostDistribution(vendorcd, vendorExists)
      }

      const filteringList = recentProductsAdd ? recentProducts : vendors

      const updatedGroupedData = groupedData.map((group) => {
        if (group.nist === nist && group.asset === asset) {
          const foundProduct = filteringList.find((n: any) => n.uuid === vendorId || n.vendor_uuid === vendorId)

          const startDate = (foundProduct as any)?.start_date || new Date().toISOString()
          const endDate = dayjs(startDate).add(1, 'year').format(dateFormat.customLabel)
          const endDateFormatted = new Date(endDate).toISOString()

          const product = {
            ...foundProduct,
            cost: 0,
            start_date: startDate,
            end_date: endDateFormatted,
            tilesTotal: (foundProduct as any)?.tilesTotal || 0
          }

          const updatedItems = [...group.items, product]
          updateDrawerProps(nist, asset, updatedItems as any)
          return {
            ...group,
            items: updatedItems
          }
        }
        return group
      })
      setGroupedData(updatedGroupedData as any)
      setAddProductOpen(false)
      setInitialSearched(false)
      dispatch(
        notification({
          open: true,
          variant: 'success',
          message: `Vendor ${editingProduct ? 'saved' : 'added'}.`
        })
      )

      const payload: AddVendorPayload = {
        itemId: vendorId,
        nist: nist.toLowerCase(),
        asset: asset.toLowerCase()
      }
      const res = await axios.post('/api/v3/ssm', payload)

      if (res.status === 201) {
        try {
          await updateProductLicense(vendorId)
          setFormData(initialFormData)
        } catch (err) {
          console.error(err)
          const message = (err as any).response?.data?.message
          setTimeout(() => {
            dispatch(
              notification({
                open: true,
                variant: 'error',
                message: message || 'Failed to add license details for vendor'
              })
            )
          }, 4000)
        }

        const products = await fetchAndGroupData(contractYear)
        if (products) {
          const newProduct = option
            ? products.find((n: any) =>
                option.vendor?.created_at ? n.uuid === option.uuid : n.uuid === option.vendor_uuid
              )
            : products.find((n: any) => n.uuid === vendorId)

          if (!vendorExists && newProduct) {
            addVendorToRecentSearches(newProduct)
          }

          if (drawerProps && drawerProps.nist && drawerProps.asset) {
            await fetchCellDetails(drawerProps.nist.toLowerCase(), drawerProps.asset.toLowerCase())
          }
        }
      }
    } catch (e) {
      console.error('Failed to add vendor', e)
      let message = 'Failed to add vendor.'
      if ((e as any).response?.data?.message) {
        message = (e as any).response.data.message
      }
      localStorage.setItem(SSM_EMPTY_BANNER_CLOSED, 'true')
      setEmptyStateBanner(true)
      await fetchAndGroupData(contractYear)
      dispatch(
        notification({
          open: true,
          variant: 'error',
          message
        })
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleSaveProduct = async () => {
    if (!selectedProduct || !drawerProps || !drawerProps.nist || !drawerProps.asset) return

    if (editingProduct) {
      setSubmitting(true)
      try {
        await updateProductLicense(selectedProduct.uuid)

        setAddProductOpen(false)
        dispatch(
          notification({
            open: true,
            variant: 'success',
            message: 'Product saved successfully.'
          })
        )

        await fetchAndGroupData(contractYear === 'Active Now' ? undefined : contractYear)
        if (drawerProps && drawerProps.nist && drawerProps.asset) {
          await fetchCellDetails(drawerProps.nist.toLowerCase(), drawerProps.asset.toLowerCase())
        }
      } catch (err) {
        console.error(err)
        const message = (err as any).response?.data?.message
        dispatch(
          notification({
            open: true,
            variant: 'error',
            message: message || 'Failed to save product'
          })
        )
      } finally {
        setSubmitting(false)
      }
    } else {
      await handleAddVendor(selectedProduct.uuid, drawerProps.nist, drawerProps.asset, false, selectedProduct)
    }
  }

  const handleAddProductClick = async () => {
    setProductPopupOpen(true)
  }

  const handleSaveCustomVendor = async () => {
    if (!drawerProps || !drawerProps.nist || !drawerProps.asset || !customVendorId) return

    setSubmitting(true)

    const vendorExists = allProducts.find((n) => n.uuid === customVendorId)
    const selectedVendor = customVendorOptions && customVendorOptions.length > 0 ? customVendorOptions[0] : null

    if (vendorExists) {
      updateCostDistribution(CostDistribution.Even, vendorExists)
    }
    setEmptyStateBanner(false)
    localStorage.setItem(SSM_EMPTY_BANNER_CLOSED, 'false')

    try {
      const payload: AddVendorPayload = {
        itemId: customVendorId,
        nist: drawerProps.nist.toLowerCase(),
        asset: drawerProps.asset.toLowerCase()
      }
      const vendorProductPayload = {
        vendorId: customVendorId,
        productName: customVendorFormData.productName
      }

      const updatedGroupedData = groupedData.map((group) => {
        if (group.nist === drawerProps.nist && group.asset === drawerProps.asset) {
          const foundProduct = customVendorOptions.find(
            (n) => n.uuid === customVendorId || n.vendor_uuid === customVendorId
          )

          const startDate = (foundProduct as any)?.start_date || new Date().toISOString()
          const endDate = dayjs(startDate).add(1, 'year').format(dateFormat.customLabel)
          const endDateFormatted = new Date(endDate).toISOString()

          const product = {
            ...foundProduct,
            cost: 0,
            start_date: startDate,
            end_date: endDateFormatted,
            tilesTotal: 0
          }

          const updatedItems = [...group.items, product]
          updateDrawerProps(drawerProps.nist, drawerProps.asset, updatedItems as any)
          return {
            ...group,
            items: updatedItems
          }
        }
        return group
      })

      setGroupedData(updatedGroupedData as any)
      setAddingCustomVendor(false)
      setInitialSearched(false)
      dispatch(
        notification({
          open: true,
          variant: 'success',
          message: 'Vendor saved successfully.'
        })
      )

      // Helper to update license for custom vendor
      const updateCustomVendorLicense = async (vendorId: string) => {
        const licensePayload: any = {
          itemId: vendorId,
          startDate: customVendorFormData.startDate,
          endDate: customVendorFormData.endDate,
          perYear: customVendorFormData.perYear.map((c) => ({
            year: c.year,
            cost: c.licenseCost ?? 0,
            count: c.numberOfLicenses ?? 0
          }))
        }

        // Only include entityType if it has a value
        if (customVendorFormData.pricingUnits) {
          licensePayload.entityType = customVendorFormData.pricingUnits
        }

        // Only include note if it has a value
        if (customVendorFormData.note) {
          licensePayload.note = customVendorFormData.note
        }

        const cells = buildCellsPayload(costDistribution, expenseCalculationFields)
        if (cells) {
          licensePayload.cells = cells
        }

        await axios.post('/api/v3/ssm/license', licensePayload)
      }

      if (selectedVendor && selectedVendor.created_at) {
        addVendorToRecentSearches(selectedVendor)
        const res2 = await axios.post('/api/v3/ssm', payload)

        if (res2.status.toString().startsWith('20')) {
          try {
            await updateCustomVendorLicense(customVendorId)
          } catch (err) {
            console.error('Failed to update license', err)
          }
          await fetchAndGroupData(contractYear)
          if (drawerProps && drawerProps.nist && drawerProps.asset) {
            await fetchCellDetails(drawerProps.nist.toLowerCase(), drawerProps.asset.toLowerCase())
          }
        }
      } else {
        const res = await axios.post('/api/v3/vendors_products/vendor', vendorProductPayload)

        if (res.status.toString().startsWith('20')) {
          if (vendorExists) {
            addVendorToRecentSearches(vendorExists)
          } else if (selectedVendor && selectedVendor.uuid && selectedVendor.display_name) {
            addVendorToRecentSearches(selectedVendor)
          }

          const res2 = await axios.post('/api/v3/ssm', payload)

          if (res2.status.toString().startsWith('20')) {
            try {
              await updateCustomVendorLicense(customVendorId)
            } catch (err) {
              console.error('Failed to update license', err)
            }
            await fetchAndGroupData(contractYear)
            if (drawerProps && drawerProps.nist && drawerProps.asset) {
              await fetchCellDetails(drawerProps.nist.toLowerCase(), drawerProps.asset.toLowerCase())
            }
          }
        }
      }

      // Reset custom vendor form
      setCustomVendorFormData(initialCustomVendorFormData)
      setCustomVendorId('')
      setCustomVendorOptions([])
    } catch (e) {
      console.error('Failed to add vendor', e)
      let message = 'Failed to add vendor.'
      if ((e as any).response?.data?.message) {
        message = (e as any).response.data.message
      }
      localStorage.setItem(SSM_EMPTY_BANNER_CLOSED, 'true')
      setEmptyStateBanner(true)
      await fetchAndGroupData(contractYear)
      dispatch(
        notification({
          open: true,
          variant: 'error',
          message
        })
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleProductSelect = async (vendor: Vendor | null) => {
    if (!vendor) {
      return
    }

    setProductPopupOpen(false)
    setEditingProduct(false)
    setAddProductOpen(true)
    setLoadingProductData(true)

    const { nist, asset } = drawerProps

    // Fetch product data from SSM endpoint to check if it exists and get license data
    let ssmProduct: any = null
    try {
      const ssmProductRes = await axios.get(`/api/v3/ssm/product/${vendor.uuid}`)
      if (ssmProductRes.status === 200 && ssmProductRes.data) {
        ssmProduct = ssmProductRes.data
      }
    } catch (e: any) {
      // 400 with ssm_product_not_found means product doesn't exist in SSM - this is expected
      if (e.response?.status === 400 && e.response?.data?.code === 'ssm_product_not_found') {
        //
      } else {
        console.error('Failed to fetch SSM product', e)
      }
    }
    // Check if product already exists anywhere in the SSM grid
    const existingInstances = groupedData
      .flatMap((group) =>
        group.items
          .filter((item) => (item as any).uuid === vendor.uuid)
          .map((item) => ({ ...item, nist: group.nist, asset: group.asset }))
      )
      .sort((a: any, b: any) => (a.cost < b.cost ? -1 : 1)) as unknown as SSMResponseProductExtended[]

    // Product exists if found in grid OR if SSM product endpoint returned data with license
    const productExistsInGrid = existingInstances.length > 0
    const productExistsInSSM = ssmProduct && ssmProduct.license
    const productExists = productExistsInGrid || productExistsInSSM
    setIsNewProduct(!productExists)

    // If product exists, prefill form with existing license data
    if (productExists) {
      // Try to get license data from: 1) SSM product endpoint (freshest), 2) existingInstances, 3) allProducts
      // Prefer ssmProduct?.license when it has startDate/endDate as it's the most up-to-date source
      const existingProduct = existingInstances[0] as any
      const productFromAllProducts = allProducts.find((n) => n.uuid === vendor.uuid)
      const ssmLicenseHasDates = ssmProduct?.license?.startDate && ssmProduct?.license?.endDate
      const licenseSource = ssmLicenseHasDates
        ? ssmProduct?.license
        : existingProduct?.license || ssmProduct?.license || (productFromAllProducts as any)?.license

      if (licenseSource) {
        const prefillFormData = {
          startDate: licenseSource.startDate,
          endDate: licenseSource.endDate,
          perYear:
            licenseSource.perYear?.map((item: any) => ({
              licenseCost: item.cost ?? 0,
              numberOfLicenses: item.count ?? 0,
              year: item.year
            })) || [],
          note: licenseSource.note,
          pricingUnits: licenseSource.entityType
        }
        setFormData(prefillFormData)
        setInitialFormDataState(prefillFormData)
      } else {
        setFormData(initialFormData)
        setInitialFormDataState(initialFormData)
      }
    } else {
      setFormData(initialFormData)
      setInitialFormDataState(initialFormData)
    }

    if (nist && asset) {
      if (productExistsInGrid) {
        // Product exists in grid - show all existing cells plus the current cell being added to
        const expenseFields = existingInstances.map((instance) => ({
          nist: instance.nist,
          asset: instance.asset,
          name: instance.name,
          displayName: instance.display_name,
          value: ((instance as any).costBps || 0) / 100 // Convert basis points to percentage
        }))

        // Add current cell if not already in the list
        const currentCellExists = expenseFields.some((field) => field.nist === nist && field.asset === asset)
        if (!currentCellExists) {
          expenseFields.push({
            nist: nist,
            asset: asset,
            name: '',
            displayName: `${nist}/${asset}`,
            value: 0
          })
        }

        const existingProduct = existingInstances[0] as any
        const productFromAllProducts = allProducts.find((n) => n.uuid === vendor.uuid)
        const licenseForDistribution =
          existingProduct?.license || ssmProduct?.license || (productFromAllProducts as any)?.license
        const distribution = getCostDistribution(licenseForDistribution)

        setExpenseCalculationFields(expenseFields)
        setCostDistribution(distribution)
        setInitialExpenseCalculationFields(JSON.parse(JSON.stringify(expenseFields)))
        setInitialCostDistribution(distribution)
      } else if (productExistsInSSM && ssmProduct?.cells?.length > 0) {
        // Product exists in SSM (ended/upcoming) but not in current grid - use cells from SSM endpoint
        const expenseFields = ssmProduct.cells.map((cell: any) => {
          const cellNist = capitalizeCellSegment(cell.nist)
          const cellAsset = capitalizeCellSegment(cell.asset)
          return {
            nist: cellNist,
            asset: cellAsset,
            name: '',
            displayName: `${cellNist}/${cellAsset}`,
            value: (cell.costBps || 0) / 100 // Convert basis points to percentage
          }
        })

        // Add current cell if not already in the list
        const currentCellExists = expenseFields.some((field: any) => field.nist === nist && field.asset === asset)
        if (!currentCellExists) {
          expenseFields.push({
            nist: nist,
            asset: asset,
            name: '',
            displayName: `${nist}/${asset}`,
            value: 0
          })
        }

        const distribution = getCostDistribution(ssmProduct?.license)

        setExpenseCalculationFields(expenseFields)
        setCostDistribution(distribution)
        setInitialExpenseCalculationFields(JSON.parse(JSON.stringify(expenseFields)))
        setInitialCostDistribution(distribution)
      } else {
        // New product - just set current cell (but Cost Distribution will be hidden)
        const initialExpenseField = {
          nist: nist,
          asset: asset,
          displayName: `${nist}/${asset}`,
          value: 0
        }
        setExpenseCalculationFields([initialExpenseField])
        setCostDistribution(CostDistribution.Even)
        setInitialExpenseCalculationFields([{ ...initialExpenseField }])
        setInitialCostDistribution(CostDistribution.Even)
      }
    }

    // Use ssmProduct if available, otherwise use the vendor object
    const productToSelect = ssmProduct || allProducts.find((n) => n.uuid === vendor.uuid)
    if (productToSelect) {
      setSelectedProduct(productToSelect)
    } else if (nist && asset) {
      addVendorToRecentSearches(vendor)
      setSelectedProduct({
        ...(vendor as any)
      })
    }

    setLoadingProductData(false)
  }

  const handleBack = () => {
    setAddProductOpen(false)
    setAddingCustomVendor(false)
    setCustomVendorOptions([])
  }

  const handleBackFromCustomVendor = () => {
    setAddingCustomVendor(false)
    setCustomVendorOptions([])
  }

  const handleEditProduct = async (product: SSMCellProduct) => {
    const editFormData = {
      startDate: product.license ? product.license.startDate : undefined,
      endDate: product.license ? product.license.endDate : undefined,
      perYear:
        product.license && product.license.perYear && product.license.perYear.length > 0
          ? product.license.perYear.map((item) => ({
              licenseCost: item.cost ?? 0,
              numberOfLicenses: item.count ?? 0,
              year: item.year
            }))
          : [],
      note: product.license ? (product.license as any).note : undefined,
      pricingUnits: product.license ? (product.license.entityType as any) : undefined
    }
    setFormData(editFormData)
    setInitialFormDataState(editFormData)

    setProductPopupOpen(false)
    setAddProductOpen(true)
    setEditingProduct(true)
    setIsNewProduct(false) // Editing existing product, not a new one
    setSelectedProduct(product as any)
    setLoadingProductData(true)

    // Get product instances - works for both active and ended/upcoming products
    let flattenedInstances = getProductInstances(product)

    // If no instances found (cache may not be ready), fetch directly from API
    if (flattenedInstances.length === 0) {
      try {
        const res = await axios.get(`/api/v3/ssm/product/${product.uuid}`)
        if (res.status === 200 && res.data?.cells?.length > 0) {
          flattenedInstances = res.data.cells.map((c: any) => ({
            uuid: product.uuid,
            name: product.name,
            display_name: product.display_name,
            nist: capitalizeCellSegment(c.nist),
            asset: capitalizeCellSegment(c.asset),
            costBps: c.costBps || 0,
            cost: c.cost ?? 0
          })) as unknown as SSMResponseProductExtended[]
        }
      } catch {
        // Product may not exist in SSM anymore
      }
    }

    const expenseFields = flattenedInstances.map((instance) => ({
      nist: instance.nist,
      asset: instance.asset,
      name: instance.name,
      displayName: instance.display_name,
      value: ((instance as any).costBps || 0) / 100 // Convert basis points to percentage
    }))
    const distribution = getCostDistribution(product.license)

    setExpenseCalculationFields(expenseFields)
    setCostDistribution(distribution)
    setInitialExpenseCalculationFields(JSON.parse(JSON.stringify(expenseFields)))
    setInitialCostDistribution(distribution)
    setLoadingProductData(false)
  }

  const handleOpenVendorPage = (product: SSMCellProduct) => {
    setProductPopupOpen(false)
    // A vendor row carries no nested vendor — the row is the vendor, so its own url is that page.
    const vendorUrl = product.vendor?.url || (!product.is_product ? product.url : '')
    if (vendorUrl) window.open(vendorUrl, '_blank')
  }

  const handleOpenProductPage = (product: SSMCellProduct) => {
    setProductPopupOpen(false)
    if (product.is_product && product.url) window.open(product.url, '_blank')
  }

  const handleRemoveFromCell = async (product: SSMCellProduct) => {
    if (!cell) return

    setProductPopupOpen(false)
    setConfirmRemoveProps({
      product,
      cell,
      open: true,
      type: 'local'
    })
  }

  const handleRemoveFromSSM = (product: SSMCellProduct) => {
    if (!cell) return

    setProductPopupOpen(false)
    const affectedCells = findProductCells(product.uuid, groupedData)

    setConfirmRemoveProps({
      product,
      cell,
      open: true,
      type: 'global',
      affectedCells
    })
  }

  const hasFormChanged = useMemo(() => {
    if (!editingProduct) return true

    if (formData.startDate !== initialFormDataState.startDate) return true
    if (formData.endDate !== initialFormDataState.endDate) return true
    if (formData.pricingUnits !== initialFormDataState.pricingUnits) return true
    if (formData.note !== initialFormDataState.note) return true

    if (formData.perYear.length !== initialFormDataState.perYear.length) return true

    for (let i = 0; i < formData.perYear.length; i++) {
      if (
        formData.perYear[i].licenseCost !== initialFormDataState.perYear[i]?.licenseCost ||
        formData.perYear[i].numberOfLicenses !== initialFormDataState.perYear[i]?.numberOfLicenses ||
        formData.perYear[i].year !== initialFormDataState.perYear[i]?.year
      ) {
        return true
      }
    }

    // Check cost distribution changes
    if (costDistribution !== initialCostDistribution) return true

    if (expenseCalculationFields.length !== initialExpenseCalculationFields.length) return true

    for (let i = 0; i < expenseCalculationFields.length; i++) {
      if (
        expenseCalculationFields[i].value !== initialExpenseCalculationFields[i]?.value ||
        expenseCalculationFields[i].nist !== initialExpenseCalculationFields[i]?.nist ||
        expenseCalculationFields[i].asset !== initialExpenseCalculationFields[i]?.asset
      ) {
        return true
      }
    }

    return false
  }, [
    editingProduct,
    formData,
    initialFormDataState,
    costDistribution,
    initialCostDistribution,
    expenseCalculationFields,
    initialExpenseCalculationFields
  ])

  // Calculate Full History budget total (sum of displayCost for all products)
  const fullHistoryBudget = useMemo(() => {
    if (!cell) return 0

    return cell.products.reduce((total, product) => {
      const perYear = product.license?.perYear
      if (perYear && perYear.length > 0) {
        if (product.status === SSMProductStatus.Ended) {
          // For Ended products, show last year's cost
          return total + (perYear[perYear.length - 1].cost ?? 0)
        }
        if (product.status === SSMProductStatus.Upcoming) {
          // For Upcoming products, show first year's cost
          return total + (perYear[0].cost ?? 0)
        }
      }
      // Default: use cellCost for Ongoing products
      return total + (product.cellCost ?? 0)
    }, 0)
  }, [cell])

  if (fetchingCell) {
    return (
      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
        <Wrapper className="ssm-drawer-wrapper">
          <ProductsContainer>
            <ProductSkeleton>
              <Skeleton
                variant="rectangular"
                component="div"
                animation="wave"
                width="100%"
                height="100%"
                className="card-skeleton-inner"
              />
            </ProductSkeleton>
            <Box sx={{ my: 1 }} />
            <ProductSkeleton>
              <Skeleton
                variant="rectangular"
                component="div"
                animation="wave"
                width="100%"
                height="100%"
                className="card-skeleton-inner"
              />
            </ProductSkeleton>
            <Box sx={{ my: 1 }} />

            {Array.from(Array(4).keys()).map((key) => (
              <ProductSkeleton key={key}>
                <Skeleton
                  variant="rectangular"
                  component="div"
                  animation="wave"
                  width="100%"
                  height="100%"
                  className="card-skeleton-inner"
                />
              </ProductSkeleton>
            ))}
          </ProductsContainer>
        </Wrapper>
      </Drawer>
    )
  }
  if (!cell) {
    return (
      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer} ModalProps={{ keepMounted: true }}>
        {/* Hidden input to satisfy useAutocomplete ref */}
        <input style={{ display: 'none' }} {...getInputProps()} />
        <Wrapper className="ssm-drawer-wrapper">
          <div>No data</div>
        </Wrapper>
      </Drawer>
    )
  }
  const sortedProducts = [...cell.products].sort((a, b) =>
    (a?.display_name || '').localeCompare(b?.display_name || '', undefined, { sensitivity: 'base' })
  )
  return (
    <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer} ModalProps={{ keepMounted: true }}>
      {/* Hidden input to satisfy useAutocomplete ref when SearchProducts is not rendered */}
      {!addProductOpen && <input style={{ display: 'none' }} {...getInputProps()} />}
      <Wrapper className="ssm-drawer-wrapper">
        <Box className="header">
          {(addProductOpen || addingCustomVendor) && (
            <IconButton className="back-button" onClick={handleBack}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M15.75 19.5L8.25 12L15.75 4.5"
                  stroke="#E9E0E4"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </IconButton>
          )}
          {addProductOpen && selectedProduct && (
            <IconButton className="add-product-logo">
              {!selectedProduct.logo_path ||
              selectedProduct.logo_path === 'N/A' ||
              selectedProduct.logo_path.includes('/N/A') ||
              selectedProduct.logo_path.includes('/null') ? (
                <Box className="missing-vendor-logo">
                  {selectedProduct.vendor && selectedProduct.vendor.display_name
                    ? selectedProduct.vendor.display_name[0]
                    : selectedProduct.display_name
                    ? selectedProduct.display_name[0]
                    : ''}
                </Box>
              ) : (
                <img src={getIntegrationLogoSSM(selectedProduct.logo_path)} alt="" />
              )}
            </IconButton>
          )}
          <Typography>
            {addingCustomVendor
              ? 'Add New Product/Vendor'
              : addProductOpen && selectedProduct
              ? `${editingProduct ? 'Edit' : 'Add'} ${
                  selectedProduct.vendor?.display_name ? `${selectedProduct.vendor.display_name} ` : ''
                }${selectedProduct.display_name}`
              : `${nist}/${asset}`}
          </Typography>
          <IconButton className="close-icon" onClick={closeDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>

        {!addProductOpen && !addingCustomVendor ? (
          <>
            <SSMDrawerViewToggle
              leftLabel="Selected View"
              centerLabel="Full History"
              alignment={alignment}
              onAlignmentChange={(newAlignment) => {
                setAlignment(newAlignment)
                setExpandedUuid(false)
              }}
            />
            {alignment === 'left' ? (
              <>
                {(() => {
                  const filteredProducts = sortedProducts.filter((product) =>
                    allProducts.some((p) => p.uuid === product.uuid)
                  )
                  const filteredCount = filteredProducts.length
                  return (
                    <Box className="sub-header">
                      <Box className="sub-header-left">
                        <Typography className="sub-header-title">Coverage:</Typography>
                        <Typography className="sub-header-text">
                          {filteredCount} <span>product{filteredCount === 1 ? '' : 's'}</span>
                        </Typography>
                      </Box>
                      <Box className="sub-header-right">
                        <Typography className="sub-header-title">Budget:</Typography>
                        <Typography className="sub-header-text">${cell.active_cost}</Typography>
                      </Box>
                    </Box>
                  )
                })()}
                <ProductsContainer>
                  {fetchingCell ? (
                    <>
                      {Array.from(Array(4).keys()).map((key) => (
                        <ProductSkeleton key={key}>
                          <Skeleton
                            variant="rectangular"
                            component="div"
                            animation="wave"
                            width="100%"
                            height="100%"
                            className="card-skeleton-inner"
                          />
                        </ProductSkeleton>
                      ))}
                    </>
                  ) : (
                    sortedProducts
                      .filter((product) => allProducts.some((p) => p.uuid === product.uuid))
                      .map((product) => {
                        const flattenedInstances = getProductInstances(product)

                        return (
                          <ProductAccordion
                            handleEditProduct={handleEditProduct}
                            handleOpenVendorPage={handleOpenVendorPage}
                            handleOpenProductPage={handleOpenProductPage}
                            handleRemoveFromCell={handleRemoveFromCell}
                            handleRemoveFromSSM={handleRemoveFromSSM}
                            key={product.uuid}
                            product={product}
                            expanded={expandedUuid === product.uuid}
                            productInstances={flattenedInstances}
                            onChange={(_, isExpanded) => setExpandedUuid(isExpanded ? product.uuid : false)}
                            contractYear={contractYear}
                            currentNist={nist}
                            currentAsset={asset}
                            alignment={alignment}
                          />
                        )
                      })
                  )}
                </ProductsContainer>
              </>
            ) : (
              <>
                <Box className="sub-header">
                  <Box className="sub-header-left">
                    <Typography className="sub-header-title">Coverage:</Typography>
                    <Typography className="sub-header-text">
                      {cell.products.length} <span>product{cell.products.length === 1 ? '' : 's'}</span>
                    </Typography>
                  </Box>
                  <Box className="sub-header-right">
                    <Typography className="sub-header-title">Budget:</Typography>
                    <Typography className="sub-header-text">${fullHistoryBudget.toLocaleString()}</Typography>
                  </Box>
                </Box>
                <ProductsContainer>
                  {fetchingCell ? (
                    <>
                      {Array.from(Array(4).keys()).map((key) => (
                        <ProductSkeleton key={key}>
                          <Skeleton
                            variant="rectangular"
                            component="div"
                            animation="wave"
                            width="100%"
                            height="100%"
                            className="card-skeleton-inner"
                          />
                        </ProductSkeleton>
                      ))}
                    </>
                  ) : (
                    sortedProducts.map((product) => {
                      const flattenedInstances = getProductInstances(product)

                      return (
                        <ProductAccordion
                          handleEditProduct={handleEditProduct}
                          handleOpenVendorPage={handleOpenVendorPage}
                          handleOpenProductPage={handleOpenProductPage}
                          handleRemoveFromCell={handleRemoveFromCell}
                          handleRemoveFromSSM={handleRemoveFromSSM}
                          key={product.uuid}
                          product={product}
                          expanded={expandedUuid === product.uuid}
                          productInstances={flattenedInstances}
                          onChange={(_, isExpanded) => setExpandedUuid(isExpanded ? product.uuid : false)}
                          contractYear={contractYear}
                          currentNist={nist}
                          currentAsset={asset}
                          alignment={alignment}
                        />
                      )
                    })
                  )}
                </ProductsContainer>
              </>
            )}
          </>
        ) : addProductOpen ? (
          <>
            <AddProductForm
              formData={formData}
              setFormData={setFormData}
              productInstances={expenseCalculationFields}
              asset={allProducts[0]}
              editingProduct={editingProduct}
              costDistribution={costDistribution}
              setCostDistribution={setCostDistribution}
              expenseCalculationFields={expenseCalculationFields}
              setExpenseCalculationFields={setExpenseCalculationFields}
              currentNist={nist}
              currentAsset={asset}
              contractYear={contractYear}
              isNewProduct={isNewProduct}
              loading={loadingProductData}
            />
          </>
        ) : addingCustomVendor ? (
          <>
            <AddCustomProductForm
              formData={customVendorFormData}
              setFormData={setCustomVendorFormData}
              productInstances={expenseCalculationFields}
              asset={allProducts[0]}
              editingProduct={editingProduct}
              costDistribution={costDistribution}
              setCostDistribution={setCostDistribution}
              expenseCalculationFields={expenseCalculationFields}
              setExpenseCalculationFields={setExpenseCalculationFields}
              currentNist={nist}
              currentAsset={asset}
              contractYear={contractYear}
              drawerProps={drawerProps}
              setVendorId={setCustomVendorId}
              customVendorOptions={customVendorOptions}
              setCustomVendorOptions={setCustomVendorOptions}
            />
          </>
        ) : null}
      </Wrapper>

      {addProductOpen ? (
        <Box className="ssm-drawer-actions-wrapper">
          <ThemeButton themevariant="secondary" className="add-product-button" onClick={handleBack}>
            Cancel
          </ThemeButton>
          <ThemeButton
            className="add-product-button"
            onClick={handleSaveProduct}
            ref={addProductButtonRef}
            disabled={
              submitting ||
              !formData.startDate ||
              !formData.endDate ||
              !hasFormChanged ||
              (formData.note?.length || 0) > 200
            }
          >
            {editingProduct ? 'Save' : 'Add'}
          </ThemeButton>
        </Box>
      ) : addingCustomVendor ? (
        <Box className="ssm-drawer-actions-wrapper">
          <ThemeButton themevariant="secondary" className="add-product-button" onClick={handleBackFromCustomVendor}>
            Cancel
          </ThemeButton>
          <ThemeButton
            className="add-product-button"
            onClick={handleSaveCustomVendor}
            ref={addProductButtonRef}
            disabled={
              submitting ||
              !customVendorId ||
              !customVendorFormData.startDate ||
              !customVendorFormData.endDate ||
              (customVendorFormData.note?.length || 0) > 200
            }
          >
            Add
          </ThemeButton>
        </Box>
      ) : (
        <Box className="ssm-drawer-actions-wrapper">
          <ThemeButton
            className="add-product-button"
            onClick={handleAddProductClick}
            ref={addProductButtonRef}
            sx={{
              background: productPopupOpen ? `${theme.colors.btnTertiaryBg} !important` : '',
              color: productPopupOpen ? `${theme.colors.cta} !important` : ''
            }}
          >
            Add Product
          </ThemeButton>
        </Box>
      )}

      <Popper open={productPopupOpen} anchorEl={addProductButtonRef.current} sx={{ zIndex: 1301 }}>
        <>
          <ClickAwayListener
            onClickAway={() => {
              setProductPopupOpen(false)
            }}
          >
            <Paper sx={searchProductsPaperSx}>
              <SearchProducts
                buttonWidth={addProductButtonRef?.current?.clientWidth}
                loading={loading}
                setBackspacingInput={setBackspacingInput}
                initialSearched={initialSearched}
                vendors={vendors}
                inputValue={inputValue}
                getRootProps={getRootProps}
                focused={focused}
                getInputProps={getInputProps}
                setVendors={setVendors}
                emptyValue={emptyVendorValue}
                setInputValue={setInputValue}
                setLoading={setLoading}
                debouncedSearch={debouncedSearch}
                drawerProps={drawerProps}
                handleClearSearch={handleClearSearch}
                getListboxProps={getListboxProps}
                getOptionProps={getOptionProps}
                handleListKeyDown={handleListKeyDown}
                recentProducts={recentProducts}
                allProducts={allProducts}
                handleProductSelect={handleProductSelect}
                cell={cell}
                setAddingCustomVendor={setAddingCustomVendor}
                setProductPopupOpen={setProductPopupOpen}
              />
            </Paper>
          </ClickAwayListener>
        </>
      </Popper>
    </Drawer>
  )
}

export default SSMDrawer
